// Live updates by polling, as the plan says: GET /api/changes?since= every
// 20 s, and only the views whose entities moved refetch. No sockets: the API
// Lambda keeps nothing between requests.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ChangeRow, ChangesResponse, Me } from "../lib/types";
import { ApiError, get, UNAUTHORIZED_EVENT } from "./client";
import { paths } from "./paths";

export const POLL_MS = 20_000;

type Listener = { entities: string[] | "all"; fn: (changes: ChangeRow[]) => void };

const listeners = new Set<Listener>();
/** Tell matching views something moved (also used after our own writes). */
export function announce(changes: ChangeRow[] | string[]) {
  const rows: ChangeRow[] = typeof changes[0] === "string"
    ? (changes as string[]).map((entity, i) => ({ id: -i, at: new Date().toISOString(), weekStart: null, entity, action: "local", summary: "", reason: null, by: null, source: "app" }))
    : (changes as ChangeRow[]);
  for (const l of listeners) {
    const hit = l.entities === "all" ? rows : rows.filter((c) => (l.entities as string[]).includes(c.entity));
    if (hit.length) l.fn(hit);
  }
}

/** Runs the poller; mount once, high in the tree. */
export function ChangesPoller({ ms = POLL_MS }: { ms?: number }) {
  const since = useRef<number | null>(null);
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const r = await get<ChangesResponse>(paths.changes(since.current ?? 0));
        // The first answer only sets the mark: everything on screen is already fresh.
        if (since.current !== null && r.changes.length) announce(r.changes);
        since.current = r.latest;
      } catch { /* the next tick tries again */ }
    };
    tick();
    const t = setInterval(() => { if (!stop && document.visibilityState !== "hidden") tick(); }, ms);
    return () => { stop = true; clearInterval(t); };
  }, [ms]);
  return null;
}

export interface Loaded<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * GET a path, and refetch when a change in one of `entities` comes in.
 * Unknown entity names refetch everything, so a new API entity can't leave a screen stale.
 */
export function useApi<T>(path: string | null, entities: string[] | "all" = "all"): Loaded<T> {
  const [state, setState] = useState<{ data: T | null; error: ApiError | null; loading: boolean }>({ data: null, error: null, loading: !!path });
  const [n, setN] = useState(0);
  const reload = useCallback(() => setN((x) => x + 1), []);
  useEffect(() => {
    if (!path) return;
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    get<T>(path).then(
      (data) => live && setState({ data, error: null, loading: false }),
      (error: ApiError) => live && setState((s) => ({ data: s.data, error, loading: false }))
    );
    return () => { live = false; };
  }, [path, n]);
  const key = entities === "all" ? "all" : entities.join(",");
  useEffect(() => {
    const l: Listener = { entities: key === "all" ? "all" : [...key.split(","), "*"], fn: reload };
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, [key, reload]);
  return { ...state, reload };
}

// ------------------------------------------------------------------ who is signed in
type MeState = { me: Me | null; status: "loading" | "in" | "out" | "error"; error?: string; reload: () => void };
const MeContext = createContext<MeState>({ me: null, status: "loading", reload: () => {} });

export function MeProvider({ children }: { children: React.ReactNode }) {
  const [s, set] = useState<Omit<MeState, "reload">>({ me: null, status: "loading" });
  const [n, setN] = useState(0);
  const reload = useCallback(() => setN((x) => x + 1), []);
  useEffect(() => {
    get<Me>(paths.me()).then(
      (me) => set({ me, status: "in" }),
      (e: ApiError) => set({ me: null, status: e.status === 401 || e.status === 403 ? "out" : "error", error: e.message })
    );
  }, [n]);
  useEffect(() => {
    const on = () => set({ me: null, status: "out" });
    window.addEventListener(UNAUTHORIZED_EVENT, on);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, on);
  }, []);
  return <MeContext.Provider value={{ ...s, reload }}>{children}</MeContext.Provider>;
}

export const useMe = () => useContext(MeContext);

const RANK = { operator: 0, supervisor: 1, admin: 2 } as const;
/** At least this role. */
export function can(me: Me | null, role: keyof typeof RANK): boolean {
  return !!me && RANK[me.role] >= RANK[role];
}
